import type { FrontendUserItem } from '@/api/modules/frontendUser'
import type { SchemaFormItem } from '@/components/FaSchemaForm/types'
import AppDictRadio from '@/components/AppForm/DictRadio.vue'

export interface FrontendUserDetailFormState {
  username: string
  password: string
  nickname: string
  display_name: string
  realname: string
  phone: string
  email: string
  avatar: string
  gender: string | number
  birthday: string
  signature: string
  level: number
  points: number
  status: string | number
  is_logoff: string | number
  is_recommend: string | number
  province: string
  city: string
  district: string
  address: string
  intro: string
  introduce: string
  constellation: string
  occupation: string
  height: number
  show_cover: string
  photo_album: string[]
  interest_tags_text: string
  is_high: string | number
  is_new: string | number
}

interface GetFrontendUserFormItemsOptions {
  mode: 'create' | 'edit'
  isEdit: boolean
  form?: FrontendUserDetailFormState
  detail?: Partial<FrontendUserItem>
  components: {
    AppAttachmentImageUpload: any
  }
}

export default function getFormItems(options: GetFrontendUserFormItemsOptions): SchemaFormItem<FrontendUserDetailFormState>[] {
  if (options.form) {
    if (options.mode === 'create') {
      options.form.username ??= ''
      options.form.password ??= ''
      options.form.nickname ??= ''
      options.form.display_name ??= ''
      options.form.realname ??= ''
      options.form.phone ??= ''
      options.form.email ??= ''
      options.form.avatar ??= ''
      options.form.gender ??= '0'
      options.form.birthday ??= ''
      options.form.signature ??= ''
      options.form.level ??= 1
      options.form.points ??= 0
      options.form.status ??= '1'
      options.form.is_logoff ??= '0'
      options.form.is_recommend ??= '0'
      options.form.province ??= ''
      options.form.city ??= ''
      options.form.district ??= ''
      options.form.address ??= ''
      options.form.intro ??= ''
      options.form.introduce ??= ''
      options.form.constellation ??= ''
      options.form.occupation ??= ''
      options.form.height ??= 0
      options.form.show_cover ??= ''
      options.form.photo_album ??= []
      options.form.interest_tags_text ??= ''
      options.form.is_high ??= '0'
      options.form.is_new ??= '0'
    }

    if (options.mode === 'edit' && options.detail) {
      options.form.username = String(options.detail.username || '')
      options.form.password = ''
      options.form.nickname = String(options.detail.nickname || '')
      options.form.display_name = String(options.detail.display_name || '')
      options.form.realname = String(options.detail.realname || '')
      options.form.phone = String(options.detail.phone || '')
      options.form.email = String(options.detail.email || '')
      options.form.avatar = String(options.detail.avatar || '')
      options.form.gender = String(options.detail.gender || 0)
      options.form.birthday = options.detail.birthday ? String(options.detail.birthday) : ''
      options.form.signature = String(options.detail.signature || '')
      options.form.level = Number(options.detail.level || 1)
      options.form.points = Number(options.detail.points || 0)
      options.form.status = String(options.detail.status || 1)
      options.form.is_logoff = String(options.detail.is_logoff || 0)
      options.form.is_recommend = String(options.detail.is_recommend || 0)
      options.form.province = String(options.detail.province || '')
      options.form.city = String(options.detail.city || '')
      options.form.district = String(options.detail.district || '')
      options.form.address = String(options.detail.address || '')
      options.form.intro = String(options.detail.intro || '')
      options.form.introduce = String(options.detail.introduce || '')
      options.form.constellation = String(options.detail.constellation || '')
      options.form.occupation = String(options.detail.occupation || '')
      options.form.height = Number(options.detail.height || 0)
      options.form.show_cover = String(options.detail.show_cover || '')
      options.form.photo_album = Array.isArray(options.detail.photo_album) ? [...options.detail.photo_album] : []
      options.form.interest_tags_text = Array.isArray(options.detail.interest_tags) ? options.detail.interest_tags.join('、') : ''
      options.form.is_high = String(options.detail.is_high || 0)
      options.form.is_new = String(options.detail.is_new || 0)
    }
  }

  return [
    {
      label: '用户名',
      prop: 'username',
      render: 'input',
      renderProps: {
        placeholder: '请输入用户名',
      },
      itemProps: {
        rules: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 32, message: '用户名长度需在 3 到 32 位之间', trigger: 'blur' },
        ],
      },
    },
    {
      label: '密码',
      prop: 'password',
      omitWhenEmpty: options.isEdit,
      render: 'input',
      renderProps: {
        type: 'password',
        placeholder: options.isEdit ? '留空则不修改密码' : '请输入登录密码',
      },
      itemProps: {
        rules: [
          {
            validator: (_rule: unknown, value: string, callback: (error?: Error) => void) => {
              if (!options.isEdit && !value) {
                callback(new Error('请设置登录密码'))
                return
              }
              if (value && value.length < 6) {
                callback(new Error('密码至少需要 6 位'))
                return
              }
              callback()
            },
            trigger: 'blur',
          },
        ],
      },
    },
    {
      label: '昵称',
      prop: 'nickname',
      render: 'input',
      renderProps: {
        placeholder: '请输入昵称',
        maxlength: 50,
      },
    },
    {
      label: '展示名',
      prop: 'display_name',
      render: 'input',
      renderProps: {
        placeholder: '请输入前台展示名',
        maxlength: 50,
      },
    },
    {
      label: '真实姓名',
      prop: 'realname',
      render: 'input',
      renderProps: {
        placeholder: '请输入真实姓名',
        maxlength: 50,
      },
    },
    {
      label: '手机号',
      prop: 'phone',
      render: 'input',
      renderProps: {
        placeholder: '请输入手机号',
        maxlength: 20,
      },
      itemProps: {
        rules: [
          { pattern: /^$|^1\d{10}$/, message: '手机号格式不正确', trigger: 'blur' },
        ],
      },
    },
    {
      label: '邮箱',
      prop: 'email',
      render: 'input',
      renderProps: {
        placeholder: '请输入邮箱',
        maxlength: 100,
      },
      itemProps: {
        rules: [
          { type: 'email', message: '邮箱格式不正确', trigger: 'blur' },
        ],
      },
    },
    {
      label: '头像',
      prop: 'avatar',
      render: options.components.AppAttachmentImageUpload,
      renderProps: {
        max: 1,
        width: 96,
        height: 96,
        ext: ['jpg', 'jpeg', 'png', 'webp'],
      },
    },
    {
      label: '封面',
      prop: 'show_cover',
      render: options.components.AppAttachmentImageUpload,
      renderProps: {
        max: 1,
        width: 168,
        height: 96,
        ext: ['jpg', 'jpeg', 'png', 'webp'],
      },
    },
    {
      label: '性别',
      prop: 'gender',
      render: AppDictRadio,
      renderProps: {
        dictName: 'gender',
        button: true,
      },
    },
    {
      label: '生日',
      prop: 'birthday',
      render: 'datePicker',
      renderProps: {
        type: 'date',
        valueFormat: 'YYYY-MM-DD HH:mm:ss',
        placeholder: '请选择生日',
        clearable: true,
      },
    },
    {
      label: '等级',
      prop: 'level',
      render: 'number',
      renderProps: {
        min: 1,
        step: 1,
      },
    },
    {
      label: '积分',
      prop: 'points',
      render: 'number',
      renderProps: {
        min: 0,
        step: 1,
      },
    },
    {
      label: '状态',
      prop: 'status',
      render: AppDictRadio,
      renderProps: {
        dictName: 'frontend_user_status',
        button: true,
      },
    },
    {
      label: '推荐',
      prop: 'is_recommend',
      render: AppDictRadio,
      renderProps: {
        dictName: 'yes_no',
        button: true,
      },
    },
    {
      label: '注销',
      prop: 'is_logoff',
      render: AppDictRadio,
      renderProps: {
        dictName: 'yes_no',
        button: true,
      },
    },
    {
      label: '高质量用户',
      prop: 'is_high',
      render: AppDictRadio,
      renderProps: {
        dictName: 'yes_no',
        button: true,
      },
    },
    {
      label: '新人用户',
      prop: 'is_new',
      render: AppDictRadio,
      renderProps: {
        dictName: 'yes_no',
        button: true,
      },
    },
    {
      label: '所在省份',
      prop: 'province',
      render: 'input',
      renderProps: {
        placeholder: '请输入省份',
        maxlength: 100,
      },
    },
    {
      label: '所在城市',
      prop: 'city',
      render: 'input',
      renderProps: {
        placeholder: '请输入城市',
        maxlength: 100,
      },
    },
    {
      label: '所在区县',
      prop: 'district',
      render: 'input',
      renderProps: {
        placeholder: '请输入区县',
        maxlength: 100,
      },
    },
    {
      label: '详细地址',
      prop: 'address',
      render: 'input',
      cols: { xs: 24, md: 24 },
      renderProps: {
        placeholder: '请输入详细地址',
        maxlength: 255,
      },
    },
    {
      label: '职业',
      prop: 'occupation',
      render: 'input',
      renderProps: {
        placeholder: '请输入职业',
        maxlength: 50,
      },
    },
    {
      label: '星座',
      prop: 'constellation',
      render: 'input',
      renderProps: {
        placeholder: '请输入星座',
        maxlength: 30,
      },
    },
    {
      label: '身高(cm)',
      prop: 'height',
      render: 'number',
      renderProps: {
        min: 0,
        max: 300,
        step: 1,
      },
    },
    {
      label: '兴趣标签',
      prop: 'interest_tags_text',
      render: 'input',
      cols: { xs: 24, md: 24 },
      renderProps: {
        placeholder: '多个标签请用 、 或 , 分隔',
        maxlength: 255,
      },
    },
    {
      label: '相册',
      prop: 'photo_album',
      render: options.components.AppAttachmentImageUpload,
      cols: { xs: 24, md: 24 },
      renderProps: {
        multiple: true,
        max: 9,
        width: 108,
        height: 108,
        ext: ['jpg', 'jpeg', 'png', 'webp'],
      },
    },
    {
      label: '一句话简介',
      prop: 'intro',
      render: 'textarea',
      cols: { xs: 24, md: 24 },
      renderProps: {
        rows: 2,
        maxlength: 255,
        showWordLimit: true,
        placeholder: '请输入一句话简介',
      },
    },
    {
      label: '详细介绍',
      prop: 'introduce',
      render: 'textarea',
      cols: { xs: 24, md: 24 },
      renderProps: {
        rows: 4,
        maxlength: 2000,
        showWordLimit: true,
        placeholder: '请输入详细介绍',
      },
    },
    {
      label: '个性签名',
      prop: 'signature',
      render: 'textarea',
      cols: { xs: 24, md: 24 },
      renderProps: {
        rows: 4,
        maxlength: 255,
        showWordLimit: true,
        placeholder: '请输入个性签名',
      },
    },
  ]
}
